package com.essntl.features.itinerary.domain.model

import com.essntl.features.address.domain.model.AddressModel
import com.essntl.features.contact.domain.model.ContactModel
import com.essntl.features.currency.domain.model.CurrencyModel
import com.essntl.features.price.domain.model.staticprice.StaticPriceModel
import com.essntl.features.service.domain.model.service.ServiceModel
import com.essntl.features.supplier.domain.model.SupplierModel
import com.mohamedrejeb.richeditor.model.RichTextState
import core.dateformat.utils.formatTimestampToHHMM

data class ItineraryServiceModel(
    val id: String,
    val service: ServiceModel,
    val grouped: Boolean?,
    val additionalInfo: String?,
    val editedDescription: String?,
    val startDate: String,
    val endDate: String?,
    val startTime: String?,
    val endTime: String?,
    val status: Status?,
    val createdAt: String,
    val updatedAt: String?,
    val price: Int?,
    val cost: Int?,
    val internalNotes: String?,
    val supplier: SupplierModel?,
    val guestCount: Int,
    val address: AddressModel?,
    val updatedBy: String?,
    val itineraryId: String?,
    val dynamicFields: List<DynamicField>?,
    val specialNotes: String?,
    val paid: Boolean?,
    val embeddedMap: String?,
    val embeddedContent: String?,
    val files: Set<String>?,
    val linkedId: String?,
    val poSentAt: String?,
    val bookedByTa: Boolean?,
    val priceCurrency: CurrencyModel?,
    val costCurrency: CurrencyModel?,
    val supplierContact: ContactModel?,
    val meetingPoint: String?,
    val staticPrice: StaticPriceModel?,
) {
    val dynamicFieldsString
        get() : String =
            dynamicFields
                .orEmpty()
                .joinToString("\n") { (fieldName, value, _) ->
                    val capitalizeKey =
                        fieldName
                            .replace("_", " ")
                            .replaceFirstChar { it.titlecaseChar() }

                    "$capitalizeKey: $value"
                }

    fun getChangedFields(itineraryServiceModel: ItineraryServiceModel): ItineraryServiceModel =
        ItineraryServiceModel(
            id = id,
            createdAt = createdAt,
            updatedAt = updatedAt,
            service = service,
            grouped = if (grouped != itineraryServiceModel.grouped) itineraryServiceModel.grouped else null,
            startDate = if (startDate != itineraryServiceModel.startDate) itineraryServiceModel.startDate else "",
            endDate = if (endDate.orEmpty() != itineraryServiceModel.endDate.orEmpty()) itineraryServiceModel.endDate else null,
            startTime = if (formatTimestampToHHMM(startTime.orEmpty()) !=
                formatTimestampToHHMM(itineraryServiceModel.startTime.orEmpty())
            ) itineraryServiceModel.startTime else null,
            endTime = if (formatTimestampToHHMM(endTime.orEmpty())
                != formatTimestampToHHMM(itineraryServiceModel.endTime.orEmpty())
            ) itineraryServiceModel.endTime else null,
            additionalInfo =
                if (additionalInfo.orEmpty() != itineraryServiceModel.additionalInfo.orEmpty()) {
                    itineraryServiceModel.additionalInfo
                } else {
                    null
                },
            editedDescription =
                if (RichTextState().setHtml(editedDescription.orEmpty()).toHtml() !=
                    RichTextState().setHtml(itineraryServiceModel.editedDescription.orEmpty()).toHtml()
                ) {
                    itineraryServiceModel.editedDescription
                } else {
                    null
                },
            price = if (price != itineraryServiceModel.price) itineraryServiceModel.price else null,
            cost = if (cost != itineraryServiceModel.cost) itineraryServiceModel.cost else null,
            status = if (status != itineraryServiceModel.status) itineraryServiceModel.status else null,
            internalNotes =
                if (internalNotes.orEmpty() != itineraryServiceModel.internalNotes.orEmpty()) {
                    itineraryServiceModel.internalNotes
                } else {
                    null
                },
            supplier = if (supplier != itineraryServiceModel.supplier) itineraryServiceModel.supplier else null,
            supplierContact = if (supplierContact?.id !=
                itineraryServiceModel.supplierContact?.id
            ) itineraryServiceModel.supplierContact ?: ContactModel.empty
            else null,
            guestCount = if (guestCount != itineraryServiceModel.guestCount) itineraryServiceModel.guestCount else -1,
            address = if (address?.id.orEmpty() != itineraryServiceModel.address?.id.orEmpty()) itineraryServiceModel.address else null,
            updatedBy = if (updatedBy != itineraryServiceModel.updatedBy) itineraryServiceModel.updatedBy else null,
            dynamicFields =
                if (
                    dynamicFields == null &&
                    itineraryServiceModel.dynamicFields.orEmpty().all { it.value.isEmpty() }
                ) {
                    null
                } else if (dynamicFields != itineraryServiceModel.dynamicFields) {
                    itineraryServiceModel.dynamicFields
                } else {
                    null
                },
            itineraryId = itineraryId,
            specialNotes = if (specialNotes.orEmpty() != itineraryServiceModel.specialNotes.orEmpty()) itineraryServiceModel.specialNotes else null,
            paid = if (paid != itineraryServiceModel.paid) itineraryServiceModel.paid else null,
            embeddedMap = if (embeddedMap.orEmpty() != itineraryServiceModel.embeddedMap.orEmpty()) itineraryServiceModel.embeddedMap else null,
            embeddedContent = if (embeddedContent.orEmpty() != itineraryServiceModel.embeddedContent.orEmpty()) itineraryServiceModel.embeddedContent else null,
            files = if (files != itineraryServiceModel.files) itineraryServiceModel.files else null,
            linkedId = if (linkedId != itineraryServiceModel.linkedId) itineraryServiceModel.linkedId else null,
            poSentAt = if (poSentAt != itineraryServiceModel.poSentAt) itineraryServiceModel.poSentAt else null,
            bookedByTa = if (bookedByTa != itineraryServiceModel.bookedByTa) itineraryServiceModel.bookedByTa else null,
            priceCurrency = if (priceCurrency?.code != itineraryServiceModel.priceCurrency?.code) itineraryServiceModel.priceCurrency else null,
            costCurrency = if (costCurrency?.code != itineraryServiceModel.costCurrency?.code) itineraryServiceModel.costCurrency else null,
            meetingPoint = if (meetingPoint.orEmpty() != itineraryServiceModel.meetingPoint.orEmpty()) itineraryServiceModel.meetingPoint else null,
            staticPrice = if (staticPrice?.id != itineraryServiceModel.staticPrice?.id) itineraryServiceModel.staticPrice else null,
        )

    val isEmpty
        get() =
            this.copy(
                id = "",
                service = ServiceModel.empty,
                createdAt = "",
                updatedAt = null,
                itineraryId = null,
            ) == empty

    enum class Status {
        Ready,
        POSent,
        Confirmed,
        TOChange,
        Cancelled,
        Completed,
    }

    companion object {
        val empty =
            ItineraryServiceModel(
                id = "",
                service = ServiceModel.empty,
                grouped = null,
                createdAt = "",
                updatedAt = null,
                additionalInfo = null,
                editedDescription = null,
                startDate = "",
                endDate = null,
                startTime = null,
                endTime = null,
                price = null,
                status = null,
                internalNotes = null,
                supplier = null,
                supplierContact = null,
                guestCount = -1,
                address = null,
                updatedBy = null,
                itineraryId = null,
                dynamicFields = null,
                specialNotes = null,
                paid = null,
                embeddedMap = null,
                embeddedContent = null,
                files = null,
                linkedId = null,
                poSentAt = null,
                bookedByTa = null,
                cost = null,
                priceCurrency = null,
                costCurrency = null,
                meetingPoint = null,
                staticPrice = null,
            )
    }
}